import STRIPE_API_VERSION from '@stripe-internal/data-v1/schema/version';
import {isBinaryContentType} from '@stripe-internal/connect-embedded-lib';
import type {
  IFrameMessageResponse,
  IFrameProxyRequest,
  IProxiedHttpResponse,
} from '../data-layer-client/FrameMessenger';
import {
  transformHeaders,
  transformStripeHeaders,
} from '../utils/transformHeaders';
import {getDevLogger} from '../utils/getLogger';
import {DataLayerAuthError, DataLayerDisabledComponentError} from './errors';
import {
  getClaimAccountSessionPromise,
  getValidApiKeyOverrideMetadata,
} from './AccountSessionUtil';
import {
  getClaimInfo,
  abortControllerMap,
  notifyAuthStateChanged,
} from './DataLayer';
import {analytics} from './analytics';
import {R_URL_ORIGIN, QA_R_URL_ORIGIN} from './MetricsEndpoints';
import {serviceEndpoints} from '../data-layer-client/buildStripeClient';

import {authStore} from './authentication/AuthenticationStore';
import type {AuthenticationKeyType} from './authentication/types';
import {
  applyResponseFilters,
  applyRequestFilters,
} from './apiFilters/applyFilters';
import type {ConnectElementImportKeys} from '../connect/ConnectJSInterface/ConnectElementList';
import {CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS} from '../connect/ConnectJSInterface/ConnectElementList';
import type {AccountSessionClaim} from './types';
import {getFlagOverrides} from '../utils/flags/flagOverrides';
import {isInvalidOrExpiredSessionError} from '../connect/EmbeddedInvalidOrExpiredSessionContext';

/**
 * URLs the data layer can proxy are all URLs we can call with stripe SDK
 */
const allowedApiOrigins: string[] = [];
// eslint-disable-next-line no-restricted-syntax
for (const environmentUrls of Object.values(serviceEndpoints)) {
  // eslint-disable-next-line no-restricted-syntax
  for (const host of Object.values(environmentUrls)) {
    allowedApiOrigins.push(`https://${host}`);
  }
  allowedApiOrigins.push(R_URL_ORIGIN.replace('/0', ''));
  allowedApiOrigins.push(QA_R_URL_ORIGIN.replace('/0', ''));
}

const unauthenticatedAllowedApiOrigins: string[] = [
  'https://qa-errors.stripe.com',
  'https://errors.stripe.com',
];

/**
 * Get the headers for the request. Adds Stripe API metadata and the Connect.js hash
 * to the request headers.
 * @param headers - The headers to transform
 * @returns The transformed headers
 */
export const getDataLayerHeaders = (
  headers: Record<string, string>,
  removeEmptyHeaders = false,
) => {
  const claimInfo = getClaimInfo();
  return transformStripeHeaders(
    headers,
    {
      stripeVersion: STRIPE_API_VERSION,
      stripeAccount: claimInfo?.connectedAccountId,
      stripeContext: claimInfo?.senderWorkspaceId,
      stripeLivemode: claimInfo?.livemode,
      // eslint-disable-next-line camelcase
      connectJsHash: __webpack_hash__,
    },
    removeEmptyHeaders,
  );
};

/**
 * Check if the origin is a stripe dev box origin
 * @param origin - The origin to check
 * @returns True if the origin is a stripe dev box origin
 */
export function isStripeDevBoxOrigin(origin: string): boolean {
  // Example: https://jorgea-api-mydev.dev.stripe.me
  return origin.toLowerCase().endsWith('-mydev.dev.stripe.me');
}

export function isIsolatedEnvOrigin(origin: string): boolean {
  return origin.toLowerCase().endsWith('-iso.dev.stripe.me');
}
const devLogger = getDevLogger();

const fetchWrapper = async (
  frameRequest: IFrameProxyRequest,
): Promise<Response> => {
  frameRequest.request.headers = frameRequest.request.headers ?? {};
  const url = new URL(frameRequest.request.url);

  const isUrlInApiAllowList =
    allowedApiOrigins.includes(url.origin) ||
    isStripeDevBoxOrigin(url.origin) ||
    isIsolatedEnvOrigin(url.origin);
  const isUrlInUnauthenticatedApiList =
    unauthenticatedAllowedApiOrigins.includes(url.origin);

  if (!isUrlInApiAllowList && !isUrlInUnauthenticatedApiList) {
    devLogger.error(
      'The url is not in the data layer API proxy allowlist',
      url,
    );
    throw new Error(
      `The url ${url} is not in the data layer API proxy allowlist`,
    );
  }

  let authKeyType: AuthenticationKeyType | undefined;
  let {request} = frameRequest;

  if (isUrlInApiAllowList) {
    const filterResult = applyRequestFilters(request);
    request = filterResult.processedRequest;
    authKeyType = filterResult.selectedApiKeyType;
  }

  // For auth'd requests, use the account and livemode value from the claim response for the request headers
  const headers = getDataLayerHeaders(request.headers || {}, true);

  // By default, we add an abortController for each fetch request
  const abortController = new AbortController();
  abortControllerMap.set(frameRequest.id, abortController);

  const apiResponse = await fetch(request.url, {
    // @ts-expect-error - TS2322 - Type 'string | Uint8Array<ArrayBufferLike> | undefined' is not assignable to type 'BodyInit | null | undefined'. | Type 'Uint8Array<ArrayBufferLike>' is not assignable to type 'BodyInit | null | undefined'. | Type 'Uint8Array<ArrayBufferLike>' is missing the following properties from type 'URLSearchParams': size, append, delete, get, and 2 more.
    body: request.body,
    headers,
    method: request.method,
    signal: abortController.signal,
  });

  // We only log failed individual requests
  if (!apiResponse.ok) {
    analytics.track('submerchant_surfaces_api_request_finished', {
      api_request_url: frameRequest.request.url,
      api_request_method: frameRequest.request.method,
      api_response_code: apiResponse.status,
      connectElement: frameRequest.request.connectElementKey,
      uiLayerId: frameRequest.request.uiLayerId,
    });
  }

  // handle expired/invalid user session
  if (isInvalidOrExpiredSessionError(apiResponse.status)) {
    // if we get a 401 and we're using a user session, we should clear the user session and update the auth state
    if (authKeyType === 'stripe_user') {
      authStore.clear('stripe_user');
      notifyAuthStateChanged();
    }
  }

  return apiResponse;
};

const validateComponentEnabledBeforeApiCall = (
  apiKeyOverride: string | undefined,
  platformIdOverride: string | undefined,
  accountSessionClaim: AccountSessionClaim,
  connectElementKey: ConnectElementImportKeys,
) => {
  const validApiKeyOverrideMetadata = getValidApiKeyOverrideMetadata(
    apiKeyOverride,
    platformIdOverride,
  );

  // We don't validate this when overriding account session metadata (i.e. express, hosted dashboard...)
  if (validApiKeyOverrideMetadata) {
    return;
  }

  const {enabledKey, enabledOverrideFlag} =
    CONNECT_ELEMENT_IMPORTS_WITH_ENTRY_POINTS[connectElementKey];

  // We also don't validate this for any component that does not have a corresponding account session parameter
  if (!enabledKey) {
    return;
  }

  // If the component is explicitly enabled with a flag override, then it is enabled
  const flagOverrides = getFlagOverrides();
  if (enabledOverrideFlag && flagOverrides[enabledOverrideFlag]) {
    return;
  }

  if (!accountSessionClaim.components[enabledKey].enabled) {
    // Note: this `throw` will not result in a reply message being sent to the frame messenger. This is an "interrupt"
    // When the component is disabled, it will render as blank, and that is handled elsewhere
    // This is a safeguard to prevent requests from happenning when the component is disabled
    throw new DataLayerDisabledComponentError(
      `The ${connectElementKey} component is not enabled, so we will not make authenticated requests on its behalf. We are interrupting the request and will not send a reply back to the frame messenger`,
    );
  }
};

export const getFrameResponse = async (
  frameRequest: IFrameProxyRequest,
  authStateChangedCallback: () => void,
): Promise<IFrameMessageResponse> => {
  const {id, type} = frameRequest;

  if (
    !frameRequest.request.headers?.Authorization &&
    frameRequest.requestType === 'authedProxy'
  ) {
    let accountSessionClaim: AccountSessionClaim;
    try {
      accountSessionClaim = await getClaimAccountSessionPromise();
    } catch (error) {
      // Note: this `throw` will not result in a reply message being sent to the frame messenger. This is an "interrupt"
      // The error related to auth would be caught and displayed in a different codepath. This is a safeguard to prevent
      // requests from happenning when auth fails
      throw new DataLayerAuthError(
        'An error occurred while initializing the data layer or authenticating in the data layer. We are interrupting the request and will not send a reply back to the frame messenger.',
      );
    }

    const {connectElementKey} = frameRequest.request;

    // If a request is being made for a component that is disabled in account sessions, we don't make this request to avoid spamming/bad error reports for 403's
    validateComponentEnabledBeforeApiCall(
      frameRequest.metaOptions.apiKeyOverride,
      frameRequest.metaOptions.platformIdOverride,
      accountSessionClaim,
      connectElementKey,
    );
  }

  try {
    const apiResponse = await fetchWrapper(frameRequest);
    const contentType = apiResponse.headers.get('content-type');
    // There are other types where we would want to not read it as text(), but will add those as needed in the future.
    const isResponseBinary = isBinaryContentType(contentType);

    const apiResponseBody = isResponseBinary
      ? await apiResponse.arrayBuffer()
      : await apiResponse.text();

    let response: IProxiedHttpResponse = {
      ok: apiResponse.ok,
      status: apiResponse.status,
      statusText: apiResponse.statusText,
      body: apiResponseBody,
      headers: transformHeaders(apiResponse.headers),
    };
    response = applyResponseFilters(
      frameRequest.request,
      response,
      authStateChangedCallback,
    );

    return {
      id,
      type,
      requestType: 'response',
      response,
    };
  } catch (error) {
    if (error instanceof Error) {
      return {
        id,
        type,
        requestType: 'errorResponse',
        error,
      };
    } else {
      return {
        id,
        type,
        requestType: 'errorResponse',
        error: new Error('An error occurred while making the request.'),
      };
    }
  } finally {
    abortControllerMap.delete(id); // remove from abortControllerMap after promise is fulfilled/error is thrown
  }
};
